import { useState, useEffect } from 'react'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'

interface ColorPickerProps {
	value: string
	onChange: (value: string) => void
	label: string
}

// Predefined color palettes
const brandPresets = [
	'#2563eb', // Blue
	'#16a34a', // Green
	'#dc2626', // Red
	'#9333ea', // Purple
	'#ea580c', // Orange
	'#0891b2', // Cyan
]

const neutralPresets = [
	'#000000', // Black
	'#ffffff', // White
	'#64748b', // Slate
	'#6b7280', // Gray
	'#71717a', // Zinc
	'#737373', // Neutral
]

const getComplementaryColor = (hex: string): string => {
	// Validate hex color format
	if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) {
		return '#000000' // Return default color if invalid
	}

	// Convert hex to RGB
	const r = parseInt(hex.slice(1, 3), 16)
	const g = parseInt(hex.slice(3, 5), 16)
	const b = parseInt(hex.slice(5, 7), 16)

	// Get complementary color
	const rC = 255 - r
	const gC = 255 - g
	const bC = 255 - b

	// Convert back to hex
	return `#${rC.toString(16).padStart(2, '0')}${gC
		.toString(16)
		.padStart(2, '0')}${bC.toString(16).padStart(2, '0')}`
}

const getAnalogousColors = (hex: string): string[] => {
	// Validate hex color format
	if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) {
		return ['#000000', '#000000'] // Return default colors if invalid
	}

	// Convert hex to HSL
	let r = parseInt(hex.slice(1, 3), 16) / 255
	let g = parseInt(hex.slice(3, 5), 16) / 255
	let b = parseInt(hex.slice(5, 7), 16) / 255

	const max = Math.max(r, g, b)
	const min = Math.min(r, g, b)
	let h = 0
	let s = 0
	const l = (max + min) / 2

	if (max !== min) {
		const d = max - min
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0)
				break
			case g:
				h = (b - r) / d + 2
				break
			case b:
				h = (r - g) / d + 4
				break
		}
		h /= 6
	}

	// Generate analogous colors (30 degrees in each direction)
	const colors: string[] = []
	for (let angle of [-30, 30]) {
		let newH = h + angle / 360
		if (newH < 0) newH += 1
		if (newH > 1) newH -= 1

		// Convert back to RGB
		let r, g, b
		if (s === 0) {
			r = g = b = l
		} else {
			const hue2rgb = (p: number, q: number, t: number) => {
				if (t < 0) t += 1
				if (t > 1) t -= 1
				if (t < 1 / 6) return p + (q - p) * 6 * t
				if (t < 1 / 2) return q
				if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
				return p
			}

			const q = l < 0.5 ? l * (1 + s) : l + s - l * s
			const p = 2 * l - q

			r = hue2rgb(p, q, newH + 1 / 3)
			g = hue2rgb(p, q, newH)
			b = hue2rgb(p, q, newH - 1 / 3)
		}

		const toHex = (x: number) => {
			const hex = Math.round(x * 255).toString(16)
			return hex.length === 1 ? '0' + hex : hex
		}

		colors.push(`#${toHex(r)}${toHex(g)}${toHex(b)}`)
	}

	return colors
}

export function ColorPicker({ value, onChange, label }: ColorPickerProps) {
	const [recentColors, setRecentColors] = useState<string[]>([])
	const [harmonicColors, setHarmonicColors] = useState<string[]>([])

	useEffect(() => {
		// Ensure value is a valid hex color
		if (!value || !/^#[0-9A-Fa-f]{6}$/.test(value)) {
			return // Skip updating harmonic colors if invalid
		}
		// Update harmonic colors when the value changes
		const complementary = getComplementaryColor(value)
		const analogous = getAnalogousColors(value)
		setHarmonicColors([complementary, ...analogous])
	}, [value])

	const handleColorChange = (newColor: string) => {
		onChange(newColor)
		// Add to recent colors if not already present
		setRecentColors((prev) => {
			const filtered = prev.filter((c) => c !== newColor)
			return [newColor, ...filtered].slice(0, 5)
		})
	}

	const ColorButton = ({
		color,
		active = false,
	}: {
		color: string
		active?: boolean
	}) => (
		<button
			type="button"
			className={cn(
				'w-8 h-8 rounded-full border border-gray-200 transition-all hover:scale-110',
				active && 'ring-2 ring-offset-2 ring-blue-500'
			)}
			style={{ backgroundColor: color }}
			onClick={() => handleColorChange(color)}>
			{active && <Check className="w-4 h-4 text-white mx-auto" />}
		</button>
	)

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					className="w-full justify-start text-left font-normal h-auto p-3">
					<div className="flex items-center gap-2">
						<div
							className="w-6 h-6 rounded-full border"
							style={{ backgroundColor: value }}
						/>
						<div className="flex flex-col">
							<Label>{label}</Label>
							<span className="text-sm text-muted-foreground">{value}</span>
						</div>
					</div>
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-80">
				<div className="space-y-4">
					<div>
						<Label>Color</Label>
						<div className="flex gap-2 mt-2">
							<Input
								type="color"
								value={value}
								onChange={(e) => handleColorChange(e.target.value)}
								className="w-12"
							/>
							<Input
								type="text"
								value={value}
								onChange={(e) => handleColorChange(e.target.value)}
								className="flex-1"
								placeholder="#000000"
							/>
						</div>
					</div>

					{recentColors.length > 0 && (
						<div>
							<Label className="text-sm">Recently Used</Label>
							<div className="flex gap-2 mt-2">
								{recentColors.map((color) => (
									<ColorButton
										key={color}
										color={color}
										active={color === value}
									/>
								))}
							</div>
						</div>
					)}

					<div>
						<Label className="text-sm">Brand Colors</Label>
						<div className="flex flex-wrap gap-2 mt-2">
							{brandPresets.map((color) => (
								<ColorButton
									key={color}
									color={color}
									active={color === value}
								/>
							))}
						</div>
					</div>

					<div>
						<Label className="text-sm">Neutral Colors</Label>
						<div className="flex flex-wrap gap-2 mt-2">
							{neutralPresets.map((color) => (
								<ColorButton
									key={color}
									color={color}
									active={color === value}
								/>
							))}
						</div>
					</div>

					<div>
						<Label className="text-sm">Color Harmony</Label>
						<div className="flex gap-2 mt-2">
							{harmonicColors.map((color) => (
								<ColorButton
									key={color}
									color={color}
									active={color === value}
								/>
							))}
						</div>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	)
}
